import {hashChangeMap, trigger as hashChangeTrigger} from './HashChangeHandler';
import {nodeAddedMap} from './MutationHandler';
import {ensureShoelaceThemeLoaded, getShoelaceBaseUrl} from './utils';

interface TabElement extends HTMLElement {
  disabled: boolean;
  active: boolean;
  panel: string;
}
interface TabPanelElement extends HTMLElement {
  active: boolean;
  name: string;
}

interface TabGroupElement extends HTMLElement {
  show(name: string): void;
  tabs: TabElement[];
  panels: TabPanelElement[];
}

const components = new Map([
  ['sl-tab-group', `${getShoelaceBaseUrl()}components/tab-group/tab-group.js`],
  ['sl-tab', `${getShoelaceBaseUrl()}components/tab/tab.js`],
  ['sl-tab-panel', `${getShoelaceBaseUrl()}components/tab-panel/tab-panel.js`],
]);

const tagNames = [...components.keys()];
const modulesUrls = [...components.values()];
const preventHashChange = new WeakMap();
const resizeObservers = new WeakMap();

function repositionIndicator(target: TabGroupElement) {
  const tabs = target.tabs;
  const currentTab = tabs.find((x) => x.active) || tabs[0];
  const indexOfCurrent = tabs.indexOf(currentTab);
  const precedingTabs = tabs.slice(0, indexOfCurrent);
  const succeedingTabs = tabs.slice(indexOfCurrent + 1);

  const offsetLeft = precedingTabs.reduce((p, c) => p + c.clientWidth, 0);
  const offsetRight = succeedingTabs.reduce((p, c) => p + c.clientWidth, 0);

  target.style.setProperty('--sl-tab-offset-left', `${offsetLeft}px`);
  target.style.setProperty('--sl-tab-offset-right', `${offsetRight}px`);
}

function isElementTabGroup(
  element: HTMLElement | null,
): element is TabGroupElement {
  return element?.matches(tagNames[0]) ?? false;
}

function isElementTabPanel(
  element: HTMLElement | null,
): element is TabPanelElement {
  return element?.matches(tagNames[2]) ?? false;
}

export const TabsController = Object.freeze({
  tagName: tagNames[0],
  selector: tagNames.join(','),
  moduleUrl: modulesUrls[0],
  async ensureLoaded() {
    return Promise.all([
      ensureShoelaceThemeLoaded(),
      ...modulesUrls.map((x) => import(/* webpackIgnore: true */ x)),
    ]);
  },
});

nodeAddedMap.set(TabsController.selector, (target) => {
  if (!target.matches(TabsController.selector)) {
    return;
  }

  let isLoaded: Promise<any> = Promise.resolve();
  if (!customElements.get(target.localName) || isElementTabGroup(target)) {
    isLoaded = TabsController.ensureLoaded();
  }

  if (isElementTabGroup(target)) {
    isLoaded.then(() => {
      const observer = new ResizeObserver(() => {
        repositionIndicator(target);
      });
      resizeObservers.set(target, observer);
      observer.observe(target);
      repositionIndicator(target);
      // handle delayed IDs that are added after DOM ready
      hashChangeTrigger();
    });

    target.addEventListener('sl-tab-show', (e) => {
      repositionIndicator(target);
      if (target.dataset.disableHash) {
        return;
      }

      if (preventHashChange.get(target)) {
        preventHashChange.delete(target);
        return;
      }

      let showName: string | undefined = undefined;
      if (
        'detail' in e &&
        e.detail &&
        typeof e.detail === 'object' &&
        'name' in e.detail &&
        typeof e.detail.name === 'string'
      ) {
        showName = e.detail.name;
      }

      const panel = target.panels.find((x) => x.name === showName);
      if (!panel) {
        return;
      }

      // maintain current focus after hash change
      const currentFocus = document.activeElement;
      window.location.hash = `#${panel.id}`;
      if (
        currentFocus &&
        'focus' in currentFocus &&
        typeof currentFocus.focus === 'function'
      ) {
        currentFocus.focus();
      }
    });
  }
});

hashChangeMap.set(
  TabsController.selector,
  (event, target, isNew, otherTarget = null) => {
    // respond only to panels in tab-groups
    if (
      !isElementTabPanel(target) ||
      !isElementTabGroup(target.parentElement)
    ) {
      return;
    }

    let tabGroup: TabGroupElement = target.parentElement;

    TabsController.ensureLoaded().then(() => {
      if (isNew) {
        if (!target.active) {
          const name = target.name;
          tabGroup.show(name);
        }
      } else {
        // activate the first non-disabled tab panel when the hash changes to something else
        if (
          target.active &&
          (!otherTarget || !isElementTabPanel(otherTarget))
        ) {
          const panel = tabGroup.tabs.find((x) => !x.disabled)?.panel;
          if (panel) {
            preventHashChange.set(tabGroup, true);
            tabGroup.show(panel);
          }
        }
      }
    });
  },
);
